<script setup lang="ts">
import { LANDING_MEMBERSHIP } from '@/content/landing'

const labels = LANDING_MEMBERSHIP.payouts.chart

/*
 * The payout rule as a toy, drawn like the hero's piggy bank: chunky emerald outlines, soft
 * fills, a white highlight on every body and a shadow under it. Before and after, side by
 * side. The shared vault holds every member's deposit as an ingot in its tier's metal, yours
 * in blue, and pays out on the 1st and the 15th into your tray. After an approved claim your
 * ingot hops out onto the repair bill for the deductible, which is stamped paid, while the
 * coins keep dropping. It draws the shape of the rule, not amounts, so it carries no numbers.
 */
const W = 400
const H = 208
const PANEL_W = 184
const PANELS = [
  { id: 'normally', x: 8, out: false },
  { id: 'claim', x: 208, out: true },
] as const

const BODY = { x: 6, y: 28, w: 118, h: 94 }
const DOOR = { cx: 58, cy: 75, r: 39 }
const GLASS_R = 30
const FLOOR = DOOR.cy + 22
const WHEEL = { cx: 110, cy: 62, r: 9 }
const MOUTH = { x: 142, y: 117 }
const TRAY = { cx: 158, rim: 158 }
const BILL = { x: 14, y: 146, w: 88, h: 48, header: 15 }
const TAG = { x: 130, y: 48, w: 56, h: 17 }
const HOLE = { x: TAG.x + 6, y: TAG.y + TAG.h / 2 }
const TILT = -6

/* Each metal's gradient stops and outline, as whole class names. */
const METALS = {
  bronze: { shine: 'payout-vault__shine--bronze', deep: 'payout-vault__deep--bronze', ingot: 'payout-vault__ingot--bronze' },
  silver: { shine: 'payout-vault__shine--silver', deep: 'payout-vault__deep--silver', ingot: 'payout-vault__ingot--silver' },
  gold: { shine: 'payout-vault__shine--gold', deep: 'payout-vault__deep--gold', ingot: 'payout-vault__ingot--gold' },
  yours: { shine: 'payout-vault__shine--yours', deep: 'payout-vault__deep--yours', ingot: 'payout-vault__ingot--yours' },
}
type Metal = keyof typeof METALS
const INGOT_W = 14
const INGOT_H = 11
const ROW = INGOT_H + 0.5
const INGOTS: { x: number, y: number, metal: Metal }[] = [
  { x: DOOR.cx - 22.5, y: FLOOR - INGOT_H, metal: 'bronze' },
  { x: DOOR.cx - 7, y: FLOOR - INGOT_H, metal: 'silver' },
  { x: DOOR.cx + 8.5, y: FLOOR - INGOT_H, metal: 'gold' },
  { x: DOOR.cx - 14.75, y: FLOOR - INGOT_H - ROW, metal: 'gold' },
  { x: DOOR.cx + 0.75, y: FLOOR - INGOT_H - ROW, metal: 'bronze' },
]
const YOURS = { x: DOOR.cx - 7, y: FLOOR - INGOT_H - ROW * 2 }
/* Your ingot comes to rest on the bill's top edge, clear of the house and car. */
const PARKED = { x: BILL.x + BILL.w - 28, y: BILL.y - INGOT_H - 0.5 }

function ingot(x: number, y: number) {
  return `M${x + 2.5} ${y} H${x + INGOT_W - 2.5} L${x + INGOT_W} ${y + INGOT_H} H${x} Z`
}

function sheen(x: number, y: number) {
  return `M${x + 4} ${y + 2.6} H${x + INGOT_W - 5}`
}

/* Rounded, so the server and every browser's Math.sin/cos write the same markup. */
function round(v: number) {
  return Math.round(v * 100) / 100
}

function polar(cx: number, cy: number, r: number, deg: number) {
  const a = (deg * Math.PI) / 180
  return { x: round(cx + Math.cos(a) * r), y: round(cy + Math.sin(a) * r) }
}

/* A five-point star, embossed on the coins the way the piggy bank's are. */
function star(cx: number, cy: number, outer: number, inner: number) {
  return `${Array.from({ length: 10 }, (_, i) => {
    const p = polar(cx, cy, i % 2 ? inner : outer, -90 + i * 36)
    return `${i ? 'L' : 'M'}${p.x} ${p.y}`
  }).join(' ')} Z`
}

/* A four-point sparkle centred on the origin; placed by its parent group. */
function sparkle(s: number) {
  const k = s * 0.2
  return `M0 ${-s} Q${k} ${-k} ${s} 0 Q${k} ${k} 0 ${s} Q${-k} ${k} ${-s} 0 Q${-k} ${-k} 0 ${-s} Z`
}

const doorRivets = [0, 60, 120, 180, 240, 300].map(deg => polar(DOOR.cx, DOOR.cy, 35.5, deg + 30))
const bodyRivets = [
  { x: BODY.x + 11, y: BODY.y + 10 },
  { x: BODY.x + BODY.w - 11, y: BODY.y + BODY.h - 10 },
  { x: BODY.x + 11, y: BODY.y + BODY.h - 10 },
]
/* A three-spoke vault handle; the paydays tag is tied to the upper-right knob. */
const knobs = [-30, 90, 210].map(deg => polar(WHEEL.cx, WHEEL.cy, WHEEL.r + 2, deg))
const spokes = knobs.map(k => `M${WHEEL.cx} ${WHEEL.cy} L${k.x} ${k.y}`).join(' ')
const doorGlint = (() => {
  const a = polar(DOOR.cx, DOOR.cy, 35.5, 200)
  const b = polar(DOOR.cx, DOOR.cy, 35.5, 245)
  return `M${a.x} ${a.y} A35.5 35.5 0 0 1 ${b.x} ${b.y}`
})()
const string = `M${knobs[0]!.x} ${knobs[0]!.y} Q${round((knobs[0]!.x + HOLE.x) / 2)} ${HOLE.y - 7} ${HOLE.x} ${HOLE.y}`

const spout = `M${BODY.x + BODY.w - 2} 104 H${MOUTH.x - 8} Q${MOUTH.x} 104 ${MOUTH.x} 112 V${MOUTH.y - 3}`
const bowl = `M${TRAY.cx - 26} ${TRAY.rim} Q${TRAY.cx - 24} ${TRAY.rim + 15} ${TRAY.cx - 11} ${TRAY.rim + 15} H${TRAY.cx + 11} Q${TRAY.cx + 24} ${TRAY.rim + 15} ${TRAY.cx + 26} ${TRAY.rim} Z`
const stack = [0, 1, 2].map(n => ({ cx: TRAY.cx + (n === 1 ? 1.5 : -1), cy: TRAY.rim - 2 - n * 4.2 }))

/*
 * The deductible, as a repair bill: rounded top, a scalloped tear along the bottom, and a
 * header with a little house and car, so it reads as the cost of a loss to either.
 */
const b = BILL
const SCALLOPS = 7
const scallop = round(b.w / SCALLOPS)
const billBottom = b.y + b.h
const bill = [
  `M${b.x} ${b.y + 7} Q${b.x} ${b.y} ${b.x + 7} ${b.y} H${b.x + b.w - 7} Q${b.x + b.w} ${b.y} ${b.x + b.w} ${b.y + 7}`,
  `V${billBottom}`,
  ...Array.from({ length: SCALLOPS }).fill(`a${round(scallop / 2)} ${round(scallop / 2 - 1.5)} 0 0 1 ${-scallop} 0`),
  'Z',
].join(' ')
const billHeader = `M${b.x} ${b.y + b.header} V${b.y + 7} Q${b.x} ${b.y} ${b.x + 7} ${b.y} H${b.x + b.w - 7} Q${b.x + b.w} ${b.y} ${b.x + b.w} ${b.y + 7} V${b.y + b.header} Z`
const HOUSE = { x: b.x + 9, y: b.y + 3.5 }
const house = `M${HOUSE.x} ${HOUSE.y + 4.5} L${HOUSE.x + 5.5} ${HOUSE.y} L${HOUSE.x + 11} ${HOUSE.y + 4.5} M${HOUSE.x + 1.8} ${HOUSE.y + 3.2} V${HOUSE.y + 9} H${HOUSE.x + 9.2} V${HOUSE.y + 3.2}`
const houseDoor = `M${HOUSE.x + 4.2} ${HOUSE.y + 9} V${HOUSE.y + 5.8} H${HOUSE.x + 6.8} V${HOUSE.y + 9}`
const CAR = { x: b.x + 25, y: b.y + 4 }
const car = `M${CAR.x} ${CAR.y + 7} V${CAR.y + 4.6} Q${CAR.x} ${CAR.y + 3.4} ${CAR.x + 1.4} ${CAR.y + 3.2} L${CAR.x + 3.6} ${CAR.y + 2.8} L${CAR.x + 5.8} ${CAR.y} H${CAR.x + 11} L${CAR.x + 13.6} ${CAR.y + 2.8} Q${CAR.x + 15.5} ${CAR.y + 3.2} ${CAR.x + 15.5} ${CAR.y + 4.6} V${CAR.y + 7} Z`
const carWheels = [{ cx: CAR.x + 4, cy: CAR.y + 7.4 }, { cx: CAR.x + 11.5, cy: CAR.y + 7.4 }]
const lineItems = [
  { y: b.y + b.header + 18, w: 30 },
  { y: b.y + b.header + 24.5, w: 22 },
]
const STAMP = { x: b.x + b.w - 22, y: b.y + b.header + 25.5 }

/* Where the tag's centre ends up once it is tilted about its string hole. */
const tagCentre = (() => {
  const dx = TAG.w / 2 - 6
  const a = (TILT * Math.PI) / 180
  return { x: round(HOLE.x + dx * Math.cos(a)), y: round(HOLE.y + dx * Math.sin(a)) }
})()

function at(panelX: number, x: number, y: number) {
  return { left: `${round(((panelX + x) / W) * 100)}%`, top: `${round((y / H) * 100)}%` }
}

/* The coins and sparkles loop, so they only run while the sketch is on screen. */
const figure = useTemplateRef<HTMLElement>('figure')
const onScreen = useElementVisibility(figure)
</script>

<template>
  <figure ref="figure" class="payout-vault" :class="{ 'payout-vault--live': onScreen }" aria-hidden="true">
    <div class="payout-vault__stage">
      <svg :viewBox="`0 0 ${W} ${H}`" class="payout-vault__svg">
        <defs>
          <linearGradient v-for="(metal, key) in METALS" :id="`payout-vault-${key}`" :key="key" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" :class="metal.shine" />
            <stop offset="100%" :class="metal.deep" />
          </linearGradient>
          <linearGradient id="payout-vault-coin" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#fef3c7" />
            <stop offset="48%" stop-color="#fbbf24" />
            <stop offset="100%" stop-color="#b45309" />
          </linearGradient>
          <radialGradient id="payout-vault-sheen" cx="30%" cy="20%" r="78%">
            <stop offset="0%" stop-color="#fff9dd" />
            <stop offset="40%" stop-color="#fde68a" stop-opacity="0.82" />
            <stop offset="100%" stop-color="#92400e" stop-opacity="0.34" />
          </radialGradient>
          <clipPath id="payout-vault-glass">
            <circle :cx="DOOR.cx" :cy="DOOR.cy" :r="GLASS_R" />
          </clipPath>
        </defs>

        <line class="payout-vault__divider" :x1="W / 2" y1="30" :x2="W / 2" :y2="H - 8" />

        <g v-for="panel in PANELS" :key="panel.id" :transform="`translate(${panel.x} 0)`">
          <g class="payout-vault__safe" :class="{ 'payout-vault__safe--late': panel.out }" :style="{ transformOrigin: `${BODY.x + BODY.w / 2}px ${BODY.y + BODY.h + 8}px` }">
            <!-- Stubby feet, a shadow, and the rounded safe on top. -->
            <ellipse class="payout-vault__shadow" :cx="BODY.x + BODY.w / 2" :cy="BODY.y + BODY.h + 11" :rx="BODY.w / 2 + 2" ry="5" />
            <rect class="payout-vault__foot" :x="BODY.x + 14" :y="BODY.y + BODY.h - 6" width="18" height="16" rx="6" />
            <rect class="payout-vault__foot" :x="BODY.x + BODY.w - 32" :y="BODY.y + BODY.h - 6" width="18" height="16" rx="6" />
            <rect class="payout-vault__body" :x="BODY.x" :y="BODY.y" :width="BODY.w" :height="BODY.h" rx="20" />
            <ellipse class="payout-vault__highlight" :cx="BODY.x + 26" :cy="BODY.y + 9" rx="15" ry="4" />
            <circle v-for="(r, n) in bodyRivets" :key="`br-${n}`" class="payout-vault__rivet" :cx="r.x" :cy="r.y" r="2.2" />

            <!-- The big round door: a riveted ring around a porthole full of ingots. -->
            <circle class="payout-vault__door" :cx="DOOR.cx" :cy="DOOR.cy" :r="DOOR.r" />
            <path class="payout-vault__door-glint" :d="doorGlint" />
            <circle v-for="(r, n) in doorRivets" :key="`dr-${n}`" class="payout-vault__rivet" :cx="r.x" :cy="r.y" r="1.7" />
            <circle class="payout-vault__glass" :cx="DOOR.cx" :cy="DOOR.cy" :r="GLASS_R" />
            <g clip-path="url(#payout-vault-glass)">
              <rect class="payout-vault__floor" :x="DOOR.cx - GLASS_R" :y="FLOOR" :width="GLASS_R * 2" :height="GLASS_R" />
              <g v-for="(bar, n) in INGOTS" :key="n">
                <path class="payout-vault__ingot" :class="METALS[bar.metal].ingot" :d="ingot(bar.x, bar.y)" :fill="`url(#payout-vault-${bar.metal})`" />
                <path class="payout-vault__ingot-sheen" :d="sheen(bar.x, bar.y)" />
              </g>
              <g v-if="!panel.out">
                <path class="payout-vault__ingot payout-vault__ingot--yours" :d="ingot(YOURS.x, YOURS.y)" fill="url(#payout-vault-yours)" />
                <path class="payout-vault__ingot-sheen" :d="sheen(YOURS.x, YOURS.y)" />
              </g>
              <path v-else class="payout-vault__gap" :d="ingot(YOURS.x, YOURS.y)" />
            </g>
            <path class="payout-vault__glare" :d="`M${DOOR.cx - 19} ${DOOR.cy - 7} Q${DOOR.cx - 17} ${DOOR.cy - 19} ${DOOR.cx - 6} ${DOOR.cy - 23}`" />
            <circle class="payout-vault__glass-rim" :cx="DOOR.cx" :cy="DOOR.cy" :r="GLASS_R" />

            <!-- The wheel, with a paydays tag tied to one of its knobs. -->
            <path class="payout-vault__spokes" :d="spokes" />
            <circle class="payout-vault__hub" :cx="WHEEL.cx" :cy="WHEEL.cy" r="4.5" />
            <circle class="payout-vault__hub-cap" :cx="WHEEL.cx" :cy="WHEEL.cy" r="1.6" />
            <circle v-for="(k, n) in knobs" :key="`k-${n}`" class="payout-vault__knob" :cx="k.x" :cy="k.y" r="3.2" />
            <path class="payout-vault__string" :d="string" />
            <g :transform="`rotate(${TILT} ${HOLE.x} ${HOLE.y})`">
              <rect class="payout-vault__tag" :x="TAG.x" :y="TAG.y" :width="TAG.w" :height="TAG.h" rx="5" />
              <circle class="payout-vault__tag-hole" :cx="HOLE.x" :cy="HOLE.y" r="1.8" />
            </g>

            <!-- The spout. -->
            <path class="payout-vault__spout" :d="spout" />
            <path class="payout-vault__spout-bore" :d="spout" />
          </g>

          <!-- Your tray: a shadow, a short stack of coins, the bowl in front. -->
          <g class="payout-vault__tray" :style="{ transformOrigin: `${TRAY.cx}px ${TRAY.rim + 16}px` }">
            <ellipse class="payout-vault__shadow" :cx="TRAY.cx" :cy="TRAY.rim + 18" rx="27" ry="3.5" />
            <g v-for="(c, n) in stack" :key="`st-${n}`">
              <ellipse class="payout-vault__stack-edge" :cx="c.cx" :cy="c.cy + 1.8" rx="11" ry="3.8" />
              <ellipse class="payout-vault__stack-face" :cx="c.cx" :cy="c.cy" rx="11" ry="3.8" fill="url(#payout-vault-coin)" />
            </g>
            <path class="payout-vault__bowl" :d="bowl" />
            <ellipse class="payout-vault__bowl-glint" :cx="TRAY.cx - 12" :cy="TRAY.rim + 5" rx="6" ry="1.8" />
          </g>

          <g v-for="k in 2" :key="`drop-${k}`" class="payout-vault__drop" :style="{ '--k': k - 1, 'transformOrigin': `${MOUTH.x}px ${MOUTH.y + 6}px` }">
            <circle :cx="MOUTH.x" :cy="MOUTH.y + 6" r="7" fill="url(#payout-vault-coin)" />
            <circle :cx="MOUTH.x" :cy="MOUTH.y + 6" r="6.7" fill="url(#payout-vault-sheen)" opacity="0.7" />
            <circle class="payout-vault__coin-rim" :cx="MOUTH.x" :cy="MOUTH.y + 6" r="6.2" />
            <path class="payout-vault__coin-star" :d="star(MOUTH.x, MOUTH.y + 6, 3, 1.3)" />
          </g>

          <g :transform="`translate(${TRAY.cx - 12} ${TRAY.rim - 17})`">
            <path class="payout-vault__sparkle" :d="sparkle(5.5)" />
          </g>
          <g :transform="`translate(${TRAY.cx + 13} ${TRAY.rim - 21})`">
            <path class="payout-vault__sparkle payout-vault__sparkle--small" :d="sparkle(3.5)" />
          </g>

          <!-- After a claim: your ingot hops out onto the repair bill, which gets stamped paid. -->
          <g v-if="panel.out" class="payout-vault__claim">
            <ellipse class="payout-vault__shadow" :cx="BILL.x + BILL.w / 2" :cy="billBottom + 6" :rx="BILL.w / 2 - 2" ry="3.5" />
            <path class="payout-vault__bill" :d="bill" />
            <path class="payout-vault__bill-header" :d="billHeader" />
            <path class="payout-vault__bill-rule" :d="`M${BILL.x + 1.5} ${BILL.y + BILL.header} H${BILL.x + BILL.w - 1.5}`" />
            <ellipse class="payout-vault__highlight" :cx="BILL.x + 58" :cy="BILL.y + 4.5" rx="9" ry="1.8" />
            <path class="payout-vault__bill-icon" :d="house" />
            <path class="payout-vault__bill-icon" :d="houseDoor" />
            <path class="payout-vault__bill-icon" :d="car" />
            <circle v-for="(w, n) in carWheels" :key="`w-${n}`" class="payout-vault__bill-wheel" :cx="w.cx" :cy="w.cy" r="2.1" />
            <rect
              v-for="(item, n) in lineItems"
              :key="`li-${n}`"
              class="payout-vault__bill-line"
              :x="BILL.x + 9"
              :y="item.y"
              :width="item.w"
              height="3"
              rx="1.5"
            />
            <g class="payout-vault__parked" :style="{ '--from-x': `${YOURS.x - PARKED.x}px`, '--from-y': `${YOURS.y - PARKED.y}px` }">
              <path class="payout-vault__ingot payout-vault__ingot--yours" :d="ingot(PARKED.x, PARKED.y)" fill="url(#payout-vault-yours)" />
              <path class="payout-vault__ingot-sheen" :d="sheen(PARKED.x, PARKED.y)" />
            </g>
          </g>
        </g>
      </svg>

      <span class="payout-vault__caption" :style="at(PANELS[0].x, PANEL_W / 2, 11)">{{ labels.normally }}</span>
      <span class="payout-vault__caption payout-vault__caption--claim" :style="at(PANELS[1].x, PANEL_W / 2, 11)">{{ labels.afterClaim }}</span>

      <span v-for="panel in PANELS" :key="`paydays-${panel.id}`" class="payout-vault__tag-label" :style="at(panel.x, tagCentre.x, tagCentre.y)">
        {{ labels.paydays }}
      </span>
      <span class="payout-vault__yours" :style="at(PANELS[0].x, DOOR.cx, YOURS.y - 8)">{{ labels.yours }}</span>

      <span class="payout-vault__label" :style="at(PANELS[0].x, BODY.x + BODY.w / 2, BODY.y + BODY.h + 25)">{{ labels.vault }}</span>
      <span class="payout-vault__label payout-vault__label--gold" :style="at(PANELS[0].x, TRAY.cx, TRAY.rim + 30)">{{ labels.payouts }}</span>
      <span class="payout-vault__label payout-vault__label--bill" :style="at(PANELS[1].x, BILL.x + 9, BILL.y + BILL.header + 8)">{{ labels.deductible }}</span>
      <span class="payout-vault__stamp" :style="at(PANELS[1].x, STAMP.x, STAMP.y)">{{ labels.paid }}</span>
      <span class="payout-vault__label payout-vault__label--gold" :style="at(PANELS[1].x, TRAY.cx, TRAY.rim + 30)">{{ labels.stillPaid }}</span>
    </div>
  </figure>
</template>
